import React, { useState } from 'react';
import Button from '../../../components/base/Button';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

// Benefits data
const benefits = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Measurable Health Impact',
    description: 'Track lives saved, families reached, and health outcomes improved through transparent metrics and regular reporting.'
  },
  {
    icon: 'ri-team-line',
    title: 'Employee Engagement',
    description: 'Involve your team through volunteering opportunities, health camps, and awareness programs in partner communities.'
  },
  {
    icon: 'ri-award-line',
    title: 'Brand Visibility',
    description: 'Recognition across digital platforms, community events, and partnership materials reaching thousands of stakeholders.'
  },
  {
    icon: 'ri-file-chart-line',
    title: 'Comprehensive Reporting',
    description: 'Detailed quarterly reports with impact metrics, success stories, and data aligned with your CSR compliance requirements.'
  },
  {
    icon: 'ri-government-line',
    title: 'Tax Benefits',
    description: '80G certification for tax deductions and full compliance with CSR regulations under Companies Act 2013.'
  },
  {
    icon: 'ri-global-line',
    title: 'SDG Alignment',
    description: 'Contribute to UN Sustainable Development Goals 3 (Good Health) and 10 (Reduced Inequalities) with documented impact.'
  }
];

export default function CSRPartnershipsSection() {
  const [showCSRForm, setShowCSRForm] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    designation: '',
    email: '',
    phone: '',
    companyWebsite: '',
    csrBudget: '',
    customBudget: '',
    focusAreas: [] as string[],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const budgetRanges = ['₹5-10 Lakhs', '₹10-25 Lakhs', '₹25-50 Lakhs', '₹50L+'];
  const focusAreaOptions = [
    'Healthcare Access',
    'Medical Education',
    'Technology Innovation',
    'Community Health',
    'Research & Development'
  ];

  const handleFocusAreaToggle = (area: string) => {
    setFormData(prev => ({
      ...prev,
      focusAreas: prev.focusAreas.includes(area)
        ? prev.focusAreas.filter(a => a !== area)
        : [...prev.focusAreas, area]
    }));
  };

  const handleDownloadCSRDeck = () => {
    // Create a simple alert for now - you can replace this with actual PDF download
    alert('CSR Deck download will be available soon. Please contact us at partnerships@diatomiclifefoundation.org');
  };

  const handleScheduleCall = () => {
    setShowScheduleModal(true);
    setSubmitStatus('idle');
  };

  const handleCloseModal = () => {
    setShowScheduleModal(false);
    setSubmitStatus('idle');
  };

  const handleSubmitSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);
      
      // Get selected focus areas
      const focusAreas = Array.from(formData.getAll('focus_areas')).join(', ');
      
      // Create URL encoded form data
      const submitData = new URLSearchParams();
      submitData.append('contact_name', formData.get('contact_name') as string);
      submitData.append('designation', formData.get('designation') as string);
      submitData.append('company_name', formData.get('company_name') as string);
      submitData.append('industry', formData.get('industry') as string);
      submitData.append('email', formData.get('email') as string);
      submitData.append('phone', formData.get('phone') as string);
      submitData.append('csr_budget', formData.get('csr_budget') as string);
      submitData.append('preferred_time', formData.get('preferred_time') as string);
      submitData.append('focus_areas', focusAreas);
      submitData.append('requirements', formData.get('requirements') as string);

      const response = await fetch('https://readdy.ai/api/form/csr-schedule-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalBudget = formData.csrBudget === 'custom' ? formData.customBudget : formData.csrBudget;
    if (!finalBudget || !formData.companyName || !formData.email) return;

    setIsSubmitting(true);
    
    try {
      // Initialize Razorpay for CSR Partnership
      const options = {
        key: 'rzp_live_SLW5EZjmEu3Hpf',
        amount: 100000, // Minimum token amount (₹1000) for CSR partnership initiation
        currency: 'INR',
        name: 'Diatomic Life Foundation',
        description: 'CSR Partnership - Initial Commitment',
        image: 'https://public.readdy.ai/ai/img_res/ddf9244497b7103272b6ef9700a82aba.jpg',
        prefill: {
          name: formData.contactPerson,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          companyName: formData.companyName,
          designation: formData.designation,
          website: formData.companyWebsite,
          csrBudget: finalBudget,
          focusAreas: formData.focusAreas.join(', '),
          message: formData.message
        },
        theme: {
          color: '#12B6B6'
        },
        handler: async function (response: any) {
          // Payment successful
          console.log('CSR Partnership payment successful:', response);
          
          // Submit form data to Readdy form
          const formDataToSubmit = new URLSearchParams();
          formDataToSubmit.append('companyName', formData.companyName);
          formDataToSubmit.append('contactPerson', formData.contactPerson);
          formDataToSubmit.append('designation', formData.designation);
          formDataToSubmit.append('email', formData.email);
          formDataToSubmit.append('phone', formData.phone);
          formDataToSubmit.append('companyWebsite', formData.companyWebsite);
          formDataToSubmit.append('csrBudget', finalBudget);
          formDataToSubmit.append('focusAreas', formData.focusAreas.join(', '));
          formDataToSubmit.append('message', formData.message);
          formDataToSubmit.append('paymentId', response.razorpay_payment_id);
          formDataToSubmit.append('paymentStatus', 'Success');

          await fetch('https://readdy.ai/api/form/d31ru9oemi0jo797cf71', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formDataToSubmit.toString(),
          });

          setIsSubmitted(true);
          setFormData({
            companyName: '',
            contactPerson: '',
            designation: '',
            email: '',
            phone: '',
            companyWebsite: '',
            csrBudget: '',
            customBudget: '',
            focusAreas: [],
            message: ''
          });
        },
        modal: {
          ondismiss: function() {
            setIsSubmitting(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Payment initialization error:', error);
      alert('Unable to initialize payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="csr-partnerships-section" className="py-24 bg-gradient-to-br from-[#F8F9FA] to-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#12B6B6]/10 rounded-full mb-6">
              <span className="text-[#12B6B6] font-semibold text-sm">Corporate Social Responsibility</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
              Partner with Us for Impact
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Join leading organizations in creating measurable health impact through strategic CSR partnerships.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-xl text-[#12B6B6]`}></i>
                </div>
                <h3 className="text-lg font-semibold text-black mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* CSR Package Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Pilot Program */}
            <div className="bg-white border-2 border-[#12B6B6] rounded-3xl p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-black mb-2">District Pilot</h3>
                <div className="text-4xl font-bold text-[#12B6B6] mb-2">₹50L+</div>
                <div className="text-gray-600">Annual Partnership</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Sponsor entire district (10,000+ families)</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>50+ PHCs enabled with OxyAI</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Branded visibility across all materials</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Quarterly board presentations</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Employee engagement programs</span>
                </li>
              </ul>
              <Button variant="primary" size="lg" className="w-full" onClick={() => setShowCSRForm(true)}>
                Partner with Us
              </Button>
            </div>

            {/* Custom Program */}
            <div className="bg-white border border-gray-200 rounded-3xl p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-black mb-2">Custom Program</h3>
                <div className="text-4xl font-bold text-black mb-2">₹10L+</div>
                <div className="text-gray-600">Flexible Partnership</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Tailored to your CSR goals</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Specific geographic focus</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Custom reporting schedule</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Flexible branding options</span>
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-[#12B6B6] mr-3"></i>
                  <span>Scalable implementation</span>
                </li>
              </ul>
              <Button variant="secondary" size="lg" className="w-full" onClick={() => setShowCSRForm(true)}>
                Discuss Custom Program
              </Button>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="primary" size="lg" className="px-8" onClick={handleDownloadCSRDeck}>
                Download CSR Deck
              </Button>
              <Button variant="secondary" size="lg" className="px-8" onClick={handleScheduleCall}>
                Schedule CSR Call
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Call Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-black mb-2">Schedule CSR Partnership Call</h3>
                  <p className="text-gray-600">Let's discuss how we can create measurable impact together</p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>

              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-semibold text-black mb-2">Call Scheduled Successfully!</h4>
                  <p className="text-gray-600">Our partnerships team will contact you within 24 hours to confirm the details.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitSchedule} data-readdy-form id="csr-schedule-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Person Name *
                      </label>
                      <input
                        type="text"
                        name="contact_name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        placeholder="Enter full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Designation *
                      </label>
                      <input
                        type="text"
                        name="designation"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        placeholder="e.g., CSR Head, Sustainability Manager"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry *
                      </label>
                      <select
                        name="industry"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select industry</option>
                        <option value="Technology">Technology</option>
                        <option value="Financial Services">Financial Services</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Energy & Utilities">Energy & Utilities</option>
                        <option value="Retail & Consumer">Retail & Consumer</option>
                        <option value="Automotive">Automotive</option>
                        <option value="Telecommunications">Telecommunications</option>
                        <option value="Pharmaceuticals">Pharmaceuticals</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        placeholder="+91-XXXXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Annual CSR Budget Range *
                      </label>
                      <select
                        name="csr_budget"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select budget range</option>
                        <option value="₹5L - ₹25L">₹5L - ₹25L</option>
                        <option value="₹25L - ₹1Cr">₹25L - ₹1Cr</option>
                        <option value="₹1Cr - ₹5Cr">₹1Cr - ₹5Cr</option>
                        <option value="₹5Cr - ₹10Cr">₹5Cr - ₹10Cr</option>
                        <option value="₹10Cr+">₹10Cr+</option>
                        <option value="Prefer not to disclose">Prefer not to disclose</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Call Time *
                      </label>
                      <select
                        name="preferred_time"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select preferred time</option>
                        <option value="9:00 AM - 11:00 AM">9:00 AM - 11:00 AM</option>
                        <option value="11:00 AM - 1:00 PM">11:00 AM - 1:00 PM</option>
                        <option value="2:00 PM - 4:00 PM">2:00 PM - 4:00 PM</option>
                        <option value="4:00 PM - 6:00 PM">4:00 PM - 6:00 PM</option>
                        <option value="6:00 PM - 8:00 PM">6:00 PM - 8:00 PM</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CSR Focus Areas *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        'Healthcare',
                        'Education',
                        'Rural Development',
                        'Technology for Good',
                        'Women Empowerment',
                        'Environmental Sustainability'
                      ].map((area) => (
                        <label key={area} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="focus_areas"
                            value={area}
                            className="w-4 h-4 text-[#12B6B6] bg-gray-100 border-gray-300 rounded focus:ring-[#12B6B6] focus:ring-2"
                          />
                          <span className="ml-2 text-sm text-gray-700">{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specific Questions or Requirements
                    </label>
                    <textarea
                      name="requirements"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your CSR goals, preferred geographies, or any specific requirements... (max 500 characters)"
                    ></textarea>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-red-500 mr-2"></i>
                        <span className="text-red-700 text-sm">
                          There was an error scheduling your call. Please try again or contact us directly.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      className="px-6"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="px-8"
                      disabled={submitStatus === 'submitting'}
                    >
                      {submitStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Scheduling...
                        </>
                      ) : (
                        'Schedule Call'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSR Partnership Form Modal */}
      {showCSRForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-[#111111]">CSR Partnership Application</h3>
                <button 
                  onClick={() => setShowCSRForm(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 cursor-pointer"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-4xl text-green-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-[#111111] mb-4">Application Received!</h4>
                  <p className="text-lg text-gray-700 mb-6">
                    Thank you for your interest in partnering with us. Our CSR team will review your application and contact you within 2 business days.
                  </p>
                  <div className="bg-[#12B6B6]/10 rounded-xl p-6">
                    <div className="flex items-center justify-center space-x-4 text-[#12B6B6]">
                      <i className="ri-calendar-check-line text-2xl"></i>
                      <div className="text-left">
                        <div className="font-semibold">Next Steps</div>
                        <div className="text-sm">Partnership discussion call scheduled</div>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    className="mt-6"
                    onClick={() => {
                      setIsSubmitted(false);
                      setShowCSRForm(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} id="csr-partnership" data-readdy-form className="space-y-6">
                  {/* Company Information */}
                  <div>
                    <h4 className="font-bold text-[#111111] mb-4">Company Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#111111] mb-2">Company Name</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#111111] mb-2">Contact Person</label>
                          <input
                            type="text"
                            name="contactPerson"
                            value={formData.contactPerson}
                            onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#111111] mb-2">Designation</label>
                          <input
                            type="text"
                            name="designation"
                            value={formData.designation}
                            onChange={(e) => setFormData({...formData, designation: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#111111] mb-2">Email Address</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#111111] mb-2">Phone Number</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#111111] mb-2">Company Website</label>
                        <input
                          type="url"
                          name="companyWebsite"
                          value={formData.companyWebsite}
                          onChange={(e) => setFormData({...formData, companyWebsite: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                          placeholder="https://"
                        />
                      </div>
                    </div>
                  </div>

                  {/* CSR Budget */}
                  <div>
                    <h4 className="font-bold text-[#111111] mb-4">CSR Budget Range</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {budgetRanges.map((budget) => (
                        <button
                          key={budget}
                          type="button"
                          onClick={() => setFormData({...formData, csrBudget: budget, customBudget: ''})}
                          className={`p-3 rounded-xl border-2 text-center font-semibold transition-all whitespace-nowrap ${
                            formData.csrBudget === budget 
                              ? 'border-[#12B6B6] bg-[#12B6B6] text-white' 
                              : 'border-gray-200 hover:border-[#12B6B6] text-gray-700'
                          }`}
                        >
                          {budget}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, csrBudget: 'custom'})}
                        className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all whitespace-nowrap ${
                          formData.csrBudget === 'custom' 
                            ? 'border-[#12B6B6] bg-[#12B6B6] text-white' 
                            : 'border-gray-200 hover:border-[#12B6B6] text-gray-700'
                        }`}
                      >
                        Custom
                      </button>
                      {formData.csrBudget === 'custom' && (
                        <input
                          type="text"
                          name="customBudget"
                          placeholder="Enter budget range"
                          value={formData.customBudget}
                          onChange={(e) => setFormData({...formData, customBudget: e.target.value})}
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                        />
                      )}
                    </div>
                  </div>

                  {/* Focus Areas */}
                  <div>
                    <h4 className="font-bold text-[#111111] mb-4">Areas of Interest</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {focusAreaOptions.map((area) => (
                        <button
                          key={area}
                          type="button"
                          onClick={() => handleFocusAreaToggle(area)}
                          className={`p-3 rounded-xl border-2 text-left font-medium transition-all ${
                            formData.focusAreas.includes(area)
                              ? 'border-[#12B6B6] bg-[#12B6B6]/10 text-[#12B6B6]'
                              : 'border-gray-200 hover:border-[#12B6B6] text-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{area}</span>
                            {formData.focusAreas.includes(area) && (
                              <i className="ri-check-line"></i>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">Additional Information</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your CSR objectives and how you envision this partnership..."
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  <div className="flex space-x-4 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCSRForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || !formData.companyName || !formData.email || !formData.phone || !formData.csrBudget}
                      className="flex-1"
                    >
                      {isSubmitting ? 'Processing...' : 'Submit Application'}
                    </Button>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 mt-6">
                    <div className="flex items-start space-x-3">
                      <i className="ri-information-line text-[#12B6B6] text-xl mt-1"></i>
                      <div>
                        <div className="font-semibold text-[#111111] mb-1">Partnership Process</div>
                        <div className="text-sm text-gray-600">
                          A token payment of ₹1,000 will be processed to initiate the partnership discussion. This amount will be adjusted against your final CSR commitment.
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
